// src/components/TrustBadges/index.js
//
// A full-width horizontal strip of heritage trust indicators.
// Used between the Banner and About sections for traditional/sweet stores.
// Driven entirely by the config passed in — no hardcoded text.

import React from 'react';
import './index.css';

// Admin-panel icon pickers for this field save Slack/Discord-style emoji
// shortcodes (":check_mark_button:", ":delivery_truck:") rather than an
// actual emoji character, so every variant below was rendering the raw
// shortcode text verbatim instead of an icon. Resolved once here so all
// of them get real emoji without each needing its own conversion.
const EMOJI_SHORTCODES = {
    check_mark_button: '✅', white_check_mark: '✅', heavy_check_mark: '✔️', ballot_box_with_check: '☑️',
    delivery_truck: '🚚', truck: '🚚', package: '📦', box: '📦', gift: '🎁',
    star: '⭐', star2: '🌟', sparkles: '✨', '100': '💯', trophy: '🏆', medal: '🥇', medal_sports: '🏅', rosette: '🏵️',
    lock: '🔒', closed_lock_with_key: '🔐', shield: '🛡️',
    clock: '🕐', alarm_clock: '⏰', hourglass_flowing_sand: '⏳', stopwatch: '⏱️',
    moneybag: '💰', credit_card: '💳', money_with_wings: '💸',
    thumbsup: '👍', '+1': '👍', heart: '❤️', ok_hand: '👌',
    fire: '🔥', hot_pepper: '🌶️', leaves: '🍃', herb: '🌿', seedling: '🌱', recycle: '♻️',
    chef: '👨‍🍳', cook: '🍳', knife_fork_plate: '🍽️',
    phone: '📞', telephone: '📞', email: '📧', envelope: '✉️',
    house: '🏠', round_pushpin: '📍', globe_with_meridians: '🌐', earth_americas: '🌎'
};

const resolveIcon = (icon) => {
    if (typeof icon !== 'string') return icon;
    const trimmed = icon.trim();
    const match = /^:([a-zA-Z0-9_+-]+):$/.exec(trimmed);
    if (!match) return icon;
    return EMOJI_SHORTCODES[match[1]] || '✨';
};

const TrustBadges = ({ config }) => {
    const rawBadges = Array.isArray(config?.trustBadges?.items) ? config.trustBadges.items : [];
    if (rawBadges.length === 0) return null;
    const badges = rawBadges.map(b => ({ ...b, icon: resolveIcon(b.icon) }));
    const heading = config?.trustBadges?.heading || null;

    const type = (config?.templateType || 'template1').toLowerCase().replace(/-/g, '_');

    switch (type) {
        case 'template4':
        case 'traditional_sweets':
            return <TraditionalTrustBadges heading={heading} badges={badges} />;

        case 'template10':
        case 'heritage_royal':
            return <HeritageTrustBadges heading={heading} badges={badges} />;

        case 'template11':
        case 'modern_mithai':
            return <ModernTrustBadges heading={heading} badges={badges} />;

        case 'template16':
        case 'arisa_collection':
            return <SweetStoreTrustBadges heading={heading} badges={badges} />;

        default:
            break;
    }

    return (
        <section className="trust-badges-section">
            {heading && <p className="trust-badges-heading">{heading}</p>}
            <div className="trust-badges-strip">
                {badges.map((badge, i) => (
                    <React.Fragment key={i}>
                        <div className="trust-badge">
                            <span className="trust-badge__icon">{badge.icon}</span>
                            <span className="trust-badge__text">{badge.text}</span>
                        </div>
                        {i < badges.length - 1 && (
                            <span className="trust-badge__divider" aria-hidden="true">✦</span>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </section>
    );
};

const HeritageTrustBadges = ({ heading, badges }) => (
    <section className="hr-trust">
        <div className="container">
            <div className="hr-trust__strip">
                {badges.map((b, i) => (
                    <div key={i} className="hr-trust__item">
                        <span className="hr-trust__icon">{b.icon}</span>
                        <span className="hr-trust__text">{b.text}</span>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const ModernTrustBadges = ({ heading, badges }) => (
    <section className="mm-trust">
        <div className="container">
            <div className="mm-trust__grid">
                {badges.map((b, i) => (
                    <div key={i} className="mm-trust__card">
                        <div className="mm-trust__icon-box">{b.icon}</div>
                        <p className="mm-trust__text">{b.text}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const TraditionalTrustBadges = ({ heading, badges }) => (
    <section className="ts-trust">
        <div className="container">
            {heading && <h3 className="ts-trust__heading">{heading}</h3>}
            <div className="ts-trust__grid">
                {badges.map((b, i) => (
                    <div key={i} className="ts-trust__pill">
                        <span className="ts-trust__icon">{b.icon}</span>
                        <span className="ts-trust__text">{b.text}</span>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION — "Why Choose Us"
// Same cream background as every other section on this template (was
// originally a dark full-bleed band using --color-secondary, but that
// variable follows the tenant's own branding.secondary_color, which
// isn't guaranteed to be a warm tone — clashed with the rest of the
// page for tenants with a cool-toned secondary brand color).
// ═══════════════════════════════════════════════════════════════════
const SweetStoreTrustBadges = ({ heading, badges }) => (
    <section className="t16-trust">
        <div className="t16-trust__container">
            <h2 className="t16-trust__heading">{heading || 'Why Choose Us'}</h2>
            <div className="t16-trust__grid">
                {badges.map((b, i) => (
                    <div key={i} className="t16-trust__card">
                        <span className="t16-trust__icon-ring">{b.icon}</span>
                        <span className="t16-trust__text">{b.text}</span>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

export default TrustBadges;
